import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash/debounce";
import dayjs from "dayjs";
import { Button, Grid, Input, Spin, Tag, message } from "antd";
import { PlusOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { getMeterReadingPending, getMeterReadinggRecord } from "../api/host";
import MeterReadingDrawer from "../components/host/MeterReadingDrawer";

//TODO 抄表后一定概率出现未同步修改账单，需要把所有未支付账单全部修改，避免二次抄表。

const MIN_SEARCH_LEN = 3; // 房间号至少输入几位才触发搜索
const PAGE_SIZE = 20;
const RECENT_SIZE = 10; // 提交后展示的最近记录条数
const HEADER_TOP = 0; // 外层若有固定导航栏，改成它的高度
const SCROLL_THRESHOLD = 8; // 滚动方向判断阈值(px)，防手抖
const EMPTY_SEARCH = {
  list: [],
  total: 0,
  page: 1,
  loading: false,
  loadingMore: false,
};

const getReasonInfo = (reason) =>
  reason === "endValueEmpty"
    ? { text: "结算抄表", color: "gold" }
    : { text: "入住抄表", color: "red" };

const getDueInfo = (dueDate) => {
  const days = dayjs(dueDate)
    .startOf("day")
    .diff(dayjs().startOf("day"), "day");
  const dateStr = dayjs(dueDate).format(
    dayjs(dueDate).isSame(dayjs(), "year") ? "MM/DD" : "YY/MM/DD",
  );
  if (days < 0)
    return {
      dateStr,
      text: `逾期${-days}天`,
      color: days < -3 ? "red" : "gold",
    };
  if (days === 0) return { dateStr, text: "今天到期", color: "gold" };
  return { dateStr, text: `还剩${days}天`, color: "gold" };
};

// 加载更多时按 _id 去重，防止翻页期间有新记录插入导致重复
const mergeById = (oldList, newList) => {
  const seen = new Set(oldList.map((i) => i._id));
  return [...oldList, ...newList.filter((i) => !seen.has(i._id))];
};

// 找最近的可滚动祖先，找不到就是 window
const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (/(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY))
      return node;
    node = node.parentElement;
  }
  return window;
};
const css = `
.mr-page{min-height:100vh;background:
  radial-gradient(circle at 12% 6%,rgba(96,165,250,.28),transparent 42%),
  radial-gradient(circle at 92% 32%,rgba(167,139,250,.20),transparent 40%),
  linear-gradient(180deg,#eef3fb,#f4f7fa 45%,#e9f0f8);}
.mr-header{position:sticky;z-index:20;padding-top:env(safe-area-inset-top,0px);
  background:rgba(255,255,255,.72);
  -webkit-backdrop-filter:blur(16px) saturate(180%);backdrop-filter:blur(16px) saturate(180%);
  box-shadow:0 4px 16px rgba(15,23,42,.08);border-bottom:1px solid rgba(255,255,255,.6);
  transition:transform .28s cubic-bezier(.2,.8,.2,1),background-color .25s,box-shadow .25s,border-color .25s,backdrop-filter .25s,-webkit-backdrop-filter .25s;}
.mr-header.is-top{background:rgba(255,255,255,0);
  -webkit-backdrop-filter:blur(0) saturate(100%);backdrop-filter:blur(0) saturate(100%);
  box-shadow:none;border-bottom-color:rgba(148,163,184,.3);}
.mr-header.is-hidden{transform:translateY(-120%);box-shadow:none;}
.mr-header-inner{max-width:760px;margin:0 auto;display:flex;align-items:center;gap:10px;padding:10px 12px;}
.mr-main{max-width:760px;margin:0 auto;padding:12px 12px calc(24px + env(safe-area-inset-bottom,0px));}
.mr-glass{background:rgba(255,255,255,.62);
  -webkit-backdrop-filter:blur(14px) saturate(160%);backdrop-filter:blur(14px) saturate(160%);
  border:1px solid rgba(255,255,255,.75);border-radius:14px;
  box-shadow:0 2px 12px rgba(15,23,42,.05);overflow:hidden;}
.mr-row{padding:12px 14px;border-bottom:1px solid rgba(148,163,184,.22);
  -webkit-tap-highlight-color:transparent;transition:background-color .15s;}
.mr-row:last-child{border-bottom:none;}
.mr-row.clickable{cursor:pointer;}
.mr-row.clickable:active{background:rgba(59,130,246,.10);}
.mr-section{display:flex;align-items:center;gap:8px;margin:4px 4px 8px;
  font-size:12px;font-weight:500;letter-spacing:.05em;color:#64748b;}
.mr-count{padding:0 7px;border-radius:9px;background:#fffbeb;color:#d97706;font-size:11px;line-height:18px;}
.mr-empty{padding:28px 0;text-align:center;color:#94a3b8;font-size:13px;}
.mr-gap{height:16px;}
.mr-num{font-family:ui-monospace,Menlo,monospace;font-size:15px;font-weight:600;color:#334155;}
.mr-unit{margin-left:3px;font-size:12px;color:#94a3b8;}
`;

// 上滑(内容下移)隐藏、下滑显示；回到顶部时变为嵌入态。返回 [状态, lock]
function useHeaderScroll(anchorRef) {
  const [state, setState] = useState({ hidden: false, atTop: true });
  const lockedRef = useRef(false);

  useEffect(() => {
    const target = findScrollParent(anchorRef.current);
    const isWin = target === window;
    const read = () => {
      const top = isWin ? window.scrollY : target.scrollTop;
      const max = isWin
        ? document.documentElement.scrollHeight - window.innerHeight
        : target.scrollHeight - target.clientHeight;
      return Math.min(Math.max(top, 0), Math.max(max, 0)); // 夹住 iOS 回弹的越界值
    };
    let lastY = read();
    let acc = 0;
    let raf = 0;

    const update = () => {
      raf = 0;
      const y = read();
      const dy = y - lastY;
      lastY = y;
      acc = dy * acc < 0 ? dy : acc + dy; // 方向反转就重新累计
      const atTop = y <= 2;
      setState((s) => {
        let hidden = s.hidden;
        if (atTop || lockedRef.current || acc < -SCROLL_THRESHOLD)
          hidden = false;
        else if (acc > SCROLL_THRESHOLD && y > 60) hidden = true;
        return hidden === s.hidden && atTop === s.atTop ? s : { hidden, atTop };
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    target.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      target.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [anchorRef]);

  // 搜索框聚焦时锁定为显示，避免键盘弹起时顶部栏乱跑
  const lock = useCallback((locked) => {
    lockedRef.current = locked;
    if (locked) setState((s) => (s.hidden ? { ...s, hidden: false } : s));
  }, []);

  return [state, lock];
}

const roomTagStyle = {
  margin: 0,
  width: 112,
  boxSizing: "border-box",
  flexShrink: 0,
  textAlign: "center",
  borderRadius: 5,
  fontWeight: 600,
  fontSize: 13,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const DUE_TONE = { red: "#dc2626", gold: "#d97706" };

function PendingRow({ item, onClick }) {
  const reason = getReasonInfo(item.reason);
  const due = getDueInfo(item.dueDate);
  return (
    <div
      className="mr-row clickable"
      role="button"
      onClick={() => onClick(item)}
      style={{ display: "flex", alignItems: "center", gap: 8 }}
    >
      {/* minWidth 让不同位数的房间号占同样宽度，中间的抄表类型才能上下对齐 */}
      <Tag
        color="blue"
        style={{
          ...roomTagStyle,
        }}
      >
        {item.roomNumber}
      </Tag>
      <Tag
        color={reason.color}
        style={{ margin: 0, borderRadius: 5, flexShrink: 0 }}
      >
        {reason.text}
      </Tag>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: "right",
          whiteSpace: "nowrap",
          fontSize: 13,
        }}
      >
        <span style={{ color: "#94a3b8" }}>{due.dateStr}</span>
        <span
          style={{ marginLeft: 6, fontWeight: 600, color: DUE_TONE[due.color] }}
        >
          {due.text}
        </span>
      </div>
      <RightOutlined
        style={{ fontSize: 11, color: "#cbd5e1", flexShrink: 0 }}
      />
    </div>
  );
}

// 同一年只显示月日，跨年才带年份
const shortDate = (d) =>
  dayjs(d).format(dayjs(d).isSame(dayjs(), "year") ? "MM/DD" : "YY/MM/DD");

function RecordRow({ item }) {
  return (
    <div
      className="mr-row"
      style={{ display: "flex", alignItems: "center", gap: 12 }}
    >
      <Tag color="blue" style={{ ...roomTagStyle }}>
        {item.host?.roomNumber}
      </Tag>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <div>
          <span className="mr-num">{item.waterReading}</span>
          <span className="mr-unit">m³</span>
          <span className="mr-num">{item.electricReading}</span>
          <span className="mr-unit">kWh</span>
        </div>
        <div></div>
      </div>

      <div style={{ textAlign: "right", flexShrink: 0, lineHeight: 1.35 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "#475569" }}>
          {shortDate(item.readingTime)}
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>
          {dayjs(item.readingTime).format("HH:mm")}
        </div>
      </div>
    </div>
  );
}

// 列表底部哨兵：进入视口自动加载，同时保留手动按钮兜底
function LoadMore({ hasMore, loading, showEnd, onLoad }) {
  const ref = useRef(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    const el = ref.current;
    if (!hasMore || loading || !el) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && onLoadRef.current(),
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading]);

  if (!hasMore && !loading && !showEnd) return null;
  return (
    <div ref={ref} className="mr-empty" style={{ padding: "14px 0" }}>
      {loading ? (
        <Spin size="small" />
      ) : hasMore ? (
        <Button type="link" size="small" onClick={() => onLoad()}>
          加载更多
        </Button>
      ) : (
        "没有更多了"
      )}
    </div>
  );
}
export default function MeterReadingPage() {
  const compact = !Grid.useBreakpoint().sm; // 窄屏时新增按钮收成图标
  const headerRef = useRef(null);
  const [{ hidden, atTop }, lockHeader] = useHeaderScroll(headerRef);

  const [keyword, setKeyword] = useState("");
  const kw = keyword.trim();
  const isSearching = kw.length >= MIN_SEARCH_LEN;

  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [search, setSearch] = useState(EMPTY_SEARCH);
  const [drawer, setDrawer] = useState({ open: false, host: null });
  const pendingSeqRef = useRef(0);
  const searchSeqRef = useRef(0);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchPending = useCallback(async () => {
    const seq = ++pendingSeqRef.current;
    setPendingLoading(true);
    try {
      const { data } = await getMeterReadingPending();
      if (seq === pendingSeqRef.current) setPendingList(data);
    } catch (e) {
      if (seq === pendingSeqRef.current) message.error("待抄表列表加载失败");
    } finally {
      if (seq === pendingSeqRef.current) setPendingLoading(false);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await getMeterReadinggRecord({
        roomNumber: "",
        page: 1,
        pageSize: RECENT_SIZE,
      });
      setRecent(data.list);
    } catch (e) {
      message.warning("最近抄表记录刷新失败");
    }
  }, []);

  // 搜索/翻页共用；序号过期的响应直接丢弃，解决乱序
  const runSearch = useCallback(async (keywordStr, pageNum = 1) => {
    const seq = ++searchSeqRef.current;
    setSearch((s) =>
      pageNum === 1 ? { ...s, loading: true } : { ...s, loadingMore: true },
    );
    try {
      const { data } = await getMeterReadinggRecord({
        roomNumber: keywordStr,
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      if (seq !== searchSeqRef.current) return;
      setSearch((s) => ({
        list: pageNum === 1 ? data.list : mergeById(s.list, data.list),
        total: data.total,
        page: pageNum,
        loading: false,
        loadingMore: false,
      }));
    } catch (e) {
      if (seq !== searchSeqRef.current) return;
      setSearch((s) => ({ ...s, loading: false, loadingMore: false }));
      message.error("查询抄表记录失败");
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((k) => runSearch(k, 1), 300),
    [runSearch],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // ── 副作用 ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  useEffect(() => {
    if (!isSearching) {
      debouncedSearch.cancel();
      searchSeqRef.current += 1; // 作废在途请求
      setSearch(EMPTY_SEARCH);
      return;
    }
    setSearch({ ...EMPTY_SEARCH, loading: true });
    debouncedSearch(kw);
  }, [kw, isSearching, debouncedSearch]);

  // ── 事件 ───────────────────────────────────────────────────────────────────
  const openDrawer = (item = null) =>
    setDrawer({
      open: true,
      host: item ? { _id: item._id, roomNumber: item.roomNumber } : null,
    });

  const handleCreated = () => {
    fetchPending();
    loadRecent();
    if (isSearching) runSearch(kw, 1);
  };

  const handleLoadMore = () => {
    if (
      search.loading ||
      search.loadingMore ||
      search.list.length >= search.total
    )
      return;
    runSearch(kw, search.page + 1);
  };

  // ── 渲染 ───────────────────────────────────────────────────────────────────
  return (
    <div className="mr-page">
      <style>{css}</style>

      <div
        ref={headerRef}
        className={`mr-header${atTop ? " is-top" : ""}${hidden ? " is-hidden" : ""}`}
        style={{ top: HEADER_TOP }}
      >
        <div className="mr-header-inner">
          <span
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: "#0f172a",
              whiteSpace: "nowrap",
            }}
          >
            水电抄表
          </span>
          <Input
            style={{ flex: 1, minWidth: 0, borderRadius: 8 }}
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder={`房间号（至少${MIN_SEARCH_LEN}位）`}
            inputMode="numeric"
            allowClear
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onFocus={() => lockHeader(true)}
            onBlur={() => lockHeader(false)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            aria-label="新增抄表"
            onClick={() => openDrawer()}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            {compact ? null : "新增抄表"}
          </Button>
        </div>
      </div>

      <div className="mr-main">
        {isSearching ? (
          <>
            <div className="mr-glass">
              {search.loading && search.list.length === 0 ? (
                <div className="mr-empty">
                  <Spin size="small" />
                </div>
              ) : search.list.length === 0 ? (
                <div className="mr-empty">未找到该房间的抄表记录</div>
              ) : (
                search.list.map((item) => (
                  <RecordRow key={item._id} item={item} />
                ))
              )}
            </div>
            {search.list.length > 0 && (
              <LoadMore
                hasMore={search.list.length < search.total}
                loading={search.loadingMore}
                showEnd={search.total > PAGE_SIZE}
                onLoad={handleLoadMore}
              />
            )}
          </>
        ) : (
          <>
            <div className="mr-section">
              待抄表房间<span className="mr-count">{pendingList.length}</span>
            </div>
            <div className="mr-glass">
              {pendingLoading && pendingList.length === 0 ? (
                <div className="mr-empty">
                  <Spin size="small" />
                </div>
              ) : pendingList.length === 0 ? (
                <div className="mr-empty">暂无待抄表房间</div>
              ) : (
                pendingList.map((item) => (
                  <PendingRow key={item._id} item={item} onClick={openDrawer} />
                ))
              )}
            </div>
            {recent.length > 0 && (
              <>
                <div className="mr-gap" />
                <div className="mr-glass">
                  {recent.map((item) => (
                    <RecordRow key={item._id} item={item} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <MeterReadingDrawer
        open={drawer.open}
        host={drawer.host}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        onSuccess={handleCreated}
      />
    </div>
  );
}
